<template>
  <Dialog
    v-model="useDialogStore().workspaceCreate.isOpen"
    size="small-hug"
    @open="onOpen"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('bcd165')"
        :show-close="true"
        @close="close"
      />
    </template>

    <Form
      ref="formRef"
      :model="form"
      :rules="rules"
      @submit="onSubmit"
    >
      <InputBlock
        :label="useT('bcd166')"
        prop="domain"
      >
        <TextInput
          v-model="form.domain"
          name="domain"
          :placeholder="useT('bcd167')"
        />
        <div
          v-if="domainFormatted?.length"
          class="text-xs text-gray-500 mt-1 w-full"
        >
          {{ domainFormatted }}.{{ useRuntimeConfig().public.interfaceDomain }}
        </div>
      </InputBlock>

      <InputBlock
        :label="useT('bcd168')"
        prop="email"
      >
        <TextInput
          v-model="form.email"
          type="email"
          name="email"
          :placeholder="useT('bcd74')"
          @keypress="(event) => emailKeyPressHandle(event)"
        />
        <div class="text-xs text-gray-500 mt-1 w-full">
          {{ useT('bcd169') }}
        </div>
      </InputBlock>

      <InputBlock
        v-if="form.email?.length"
        :label="useT('bcd170')"
        prop="locale"
      >
        <Select
          v-model="form.locale"
          name="locale"
          :options="localeOptions"
        />
      </InputBlock>

      <InputBlock
        :label="useT('workspace.create.currency')"
        prop="currency"
      >
        <Select
          v-model="form.currency"
          name="currency"
          :options="currencyOptions"
        />
      </InputBlock>

      <InputBlock
        v-if="requiredWorkspaceFields.includes('taxNumber')"
        :label="useT('bcd309')"
        prop="taxNumber"
      >
        <TextInput
          v-model="form.taxNumber"
          name="taxNumber"
          placeholder="24721123"
        />
      </InputBlock>

      <InputBlock
        v-if="requiredWorkspaceFields.includes('vatId')"
        :label="useT('workspace.vatId')"
        prop="vatId"
      >
        <TextInput
          v-model="form.vatId"
          name="vatId"
          placeholder="DE123456789"
        />
        <div class="text-xs text-gray-500 mt-1 w-full">
          {{ useT('workspace.vatId.hint') }}
        </div>
      </InputBlock>
    </Form>

    <template
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd95')"
        :text-cancel="useT('bcd205')"
        align="right"
        :loading="isLoading"
        :disabled="!hasRules"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('workspaceCreate')"
      />
    </template>
  </Dialog>
</template>

<script setup>
// Refs
const form = ref({
  domain: '',
  email: '',
  locale: '',
  currency: 'czk',
  taxNumber: '',
  vatId: '',
});

const rules = computed(() => ({
  domain: [
    useFormRules().required,
  ],
  email: [
    useFormRules().email,
  ],
  locale: [
    useFormRules().required,
  ],
  ...Object.fromEntries(requiredWorkspaceFields.value.map((field) => [field, [useFormRules().required]])),
}));

const isLoading = ref(false);
const formRef = ref(null);

const localeOptions = ref([
  { id: 'en', text: useT('bcd172'), icon: 'custom/flag-en' },
  { id: 'cs', text: useT('bcd171'), icon: 'custom/flag-cz' },
]);

// What the workspace pays Attendu in. CZK and EUR are billed by the Czech company on the Czech
// Stripe account, USD by the US one; it cannot be changed here once the workspace exists.
const currencyOptions = ref([
  { id: 'usd', text: useT('workspace.create.currency.usd') },
  { id: 'eur', text: useT('workspace.create.currency.eur') },
  { id: 'czk', text: useT('workspace.create.currency.czk') },
]);

// Computed
// Create waits for the rules: without them the IČO or VAT ID field would be missing.
const hasRules = computed(() => Boolean(useProductsStore().billingCurrencies));

// What the chosen currency asks for, from interface's billing currency rules.
const requiredWorkspaceFields = computed(() => useProductsStore().getRequiredWorkspaceFields(form.value.currency));

const domainFormatted = computed(() => normalizeString(form.value.domain || '').replaceAll(' ', '').replace(/[^a-zA-Z0-9-]/g, ''));

// Methods
const onOpen = async() => {
  form.value = {
    domain: '',
    email: '',
    locale: localeOptions.value[0].id,
    currency: 'czk',
    taxNumber: '',
    vatId: '',
  };

  // Loaded with the layout. Without them the dialog would leave the IČO or VAT ID field out,
  // so a failed load is retried here, and the dialog closes when that fails too.
  if (!useProductsStore().billingCurrencies && !await useProductsStore().fetchBillingCurrencies()) {
    useToast.error(useT('workspace.create.rulesError'));
    useDialogStore().close('workspaceCreate');
  }
};

const onSubmit = async() => {
  isLoading.value = true;

  const response = await useWorkspacesStore().create({
    domain: domainFormatted.value,
    email: form.value.email,
    locale: form.value.locale,
    currency: form.value.currency,
    // Interface reads each only where the currency requires it.
    taxNumber: form.value.taxNumber,
    vatId: form.value.vatId,
  });

  isLoading.value = false;

  if (response) {
    useToast(useT('bcd173'));
    useDialogStore().close('workspaceCreate');
    useDialogStore().workspaceCreate.onSubmit(response);
  }
};

const emailKeyPressHandle = (event) => {
  const regex = /^[a-zA-Z0-9!@#$%^&*()_+{}[\]:;<>,.?~/\\|\-=\s]+$/;

  if (!regex.test(event.key) || event.key.trim().length === 0) {
    event.preventDefault();
  }
};
</script>
